/**
 * Configuration Manager
 * Handles all configuration options for the Process Visualization Widget
 */
export class ConfigurationManager {
    constructor(userConfig = {}) {
        this.config = this.mergeWithDefaults(userConfig);
    }

    /**
     * Get the default configuration
     */
    getDefaultConfig() {
        return {
            // Data source configuration
            dataSource: {
                type: 'api', // 'api', 'static', 'callback'
                api: {
                    baseUrl: 'http://127.0.0.1:8000',
                    endpoint: '/process_graph_description',
                    dbSource: 'source_events',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                },
                static: {
                    data: null
                },
                callback: {
                    fetchFunction: null
                }
            },

            // UI component visibility
            ui: {
                showFilters: true,
                showMetrics: true,
                showZoomControls: true,
                containerClass: 'process-visualizer'
            },

            // Visualization settings
            visualization: {
                nodeColors: {
                    default: '#3498db',
                    start: '#2ecc71',
                    end: '#e74c3c',
                    rare: '#95a5a6'
                },
                edgeColors: {
                    default: '#95a5a6',
                    highlighted: '#8e44ad'
                },
                cytoscape: {
                    wheelSensitivity: 3,
                    minZoom: 0.2,
                    maxZoom: 2.0
                }
            },

            // Filter settings (other values currently do not work correctly!)
            filters: {
                defaultNodeFilter: 100,
                defaultEdgeFilter: 100,
            },

            // Theme and styling
            theme: {
                name: 'default',
                customStyles: {} // currently not used
            },

            // Event callbacks
            callbacks: {
                onNodeClick: null,
                onNodeHover: null,
                onEdgeClick: null,
                onDataLoad: null,
                onDataError: null,
                onFilterChange: null,
                onZoomChange: null
            },

            // Messages and loading
            messages: {
                loading: 'Loading process data...',
                success: 'Process graph visualization loaded successfully!',
                error: 'Error loading process data',
                noData: 'No process data available'
            }
        };
    }

    /**
     * Merge user configuration with defaults
     */
    mergeWithDefaults(userConfig) {
        const defaultConfig = this.getDefaultConfig();
        return this.deepMerge(defaultConfig, userConfig);
    }

    /**
     * Deep merge two objects
     */
    deepMerge(target, source) {
        const result = { ...target };
        
        for (const key in source) {
            if (source.hasOwnProperty(key)) {
                if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                    result[key] = this.deepMerge(target[key] || {}, source[key]);
                } else {
                    result[key] = source[key];
                }
            }
        }
        
        return result;
    }

    /**
     * Get configuration value by path (e.g., 'ui.showFilters')
     */
    get(path) {
        return path.split('.').reduce((obj, key) => obj && obj[key], this.config);
    }

    /**
     * Set configuration value by path
     */
    set(path, value) {
        const keys = path.split('.');
        const lastKey = keys.pop();
        const target = keys.reduce((obj, key) => {
            if (!obj[key]) obj[key] = {};
            return obj[key];
        }, this.config);
        
        target[lastKey] = value;
    }

    /**
     * Get the entire configuration object
     */
    getAll() {
        return this.config;
    }

    /**
     * Update configuration with new values
     */
    update(newConfig) {
        this.config = this.deepMerge(this.config, newConfig);
    }

    /**
     * Validate configuration
     */
    validate() {
        const errors = [];

        // Validate data source
        const dataSourceType = this.get('dataSource.type');
        if (!['api', 'static', 'callback'].includes(dataSourceType)) {
            errors.push('Invalid data source type. Must be "api", "static", or "callback"');
        }

        // Validate callbacks if provided
        const callbacks = this.get('callbacks');
        for (const [key, callback] of Object.entries(callbacks)) {
            if (callback !== null && typeof callback !== 'function') {
                errors.push(`Callback ${key} must be a function or null`);
            }
        }

        // Validate colors
        const nodeColors = this.get('visualization.nodeColors');
        for (const [key, color] of Object.entries(nodeColors)) {
            if (typeof color !== 'string' || !color.startsWith('#')) {
                errors.push(`Node color ${key} must be a valid hex color`);
            }
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }
}